import { History, Minus, Plus } from 'lucide-react';
import { EstadoErro, EstadoVazio } from '@/components/patterns/states';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { dataSource } from '@/data';
import type { EventoCapability } from '@/data/types';
import { useResource } from '@/hooks/use-resource';
import { rotuloDaCapability } from '@/lib/admin-capabilities';
import { formatarDataHora, formatarRelativo } from '@/lib/format';

const LIMITE = 50;

/** Seção "Auditoria": quem concedeu/revogou o quê a quem, do mais recente para o mais antigo. */
export function PainelAuditoria() {
  const eventos = useResource<EventoCapability[]>((signal) => dataSource.listarEventosCapabilities(LIMITE, signal));

  return (
    <Card>
      {eventos.estado === 'carregando' ? (
        <ul role="status" aria-label="Carregando auditoria" className="flex flex-col gap-4 p-4">
          {Array.from({ length: 5 }).map((_, indice) => (
            <li key={indice} className="flex items-center gap-3">
              <Skeleton className="size-7 shrink-0 rounded-full" />
              <div className="flex flex-1 flex-col gap-1.5">
                <Skeleton className="h-3.5 w-3/4 max-w-md" />
                <Skeleton className="h-2.5 w-24" />
              </div>
            </li>
          ))}
        </ul>
      ) : eventos.estado === 'erro' ? (
        <EstadoErro erro={eventos.erro} aoTentarDeNovo={eventos.recarregar} />
      ) : eventos.dados && eventos.dados.length > 0 ? (
        <ol aria-label="Eventos recentes de capabilities" className="divide-y divide-border-subtle">
          {eventos.dados.map((evento) => {
            const concedeu = evento.acao === 'conceder';
            return (
              <li key={evento.id} className="flex items-start gap-3 px-4 py-3">
                <span
                  aria-hidden="true"
                  className={`mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full ${
                    concedeu ? 'bg-success-wash text-success' : 'bg-danger-wash text-danger'
                  }`}
                >
                  {concedeu ? <Plus className="size-4" /> : <Minus className="size-4" />}
                </span>
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <p className="break-words text-body">
                    <strong>{evento.ator}</strong> {concedeu ? 'concedeu' : 'revogou'}{' '}
                    <Badge tone={concedeu ? 'success' : 'danger'} className="align-middle">
                      {rotuloDaCapability(evento.capability)}
                    </Badge>{' '}
                    {concedeu ? 'a' : 'de'} <strong>{evento.alvo_email ?? evento.alvo_id}</strong>
                  </p>
                  <time
                    dateTime={evento.criado_em}
                    title={formatarDataHora(evento.criado_em)}
                    className="text-micro font-normal text-on-surface-muted"
                  >
                    {formatarRelativo(evento.criado_em)}
                  </time>
                </div>
              </li>
            );
          })}
        </ol>
      ) : (
        <EstadoVazio
          titulo="Nenhum evento registrado"
          descricao="Concessões e revogações de capability aparecem aqui, com quem fez e quando."
          acao={<History className="size-4 text-icon" aria-hidden="true" />}
        />
      )}
    </Card>
  );
}
